import React from 'react';
import { Link } from 'react-router-dom';
import { CHAIN_LABELS, CHAIN_COLORS, formatNum, getStrategyMeta } from '../../utils/masterChefUtils';

export default function PositionRow({ p, onChart }) {
  const multiple = p.currentMultiple || 1;
  const invested = p.totalInvested || 0;
  const realized = p.totalRealized || 0;
  const totalTokens = p.totalTokensAcquired || 1;
  const tokensRemaining = p.tokensRemaining || 0;

  const portionSold = totalTokens > 0 ? (totalTokens - tokensRemaining) / totalTokens : 1;
  const costBasisSold = invested * portionSold;
  const realizedPnl = realized - costBasisSold;
  const unrealizedPnl = p.unrealizedPnl || 0;
  const totalPnl = realizedPnl + unrealizedPnl;
  const totalPnlPct = invested > 0 ? (totalPnl / invested) * 100 : 0;
  const costBasisRemaining = invested * (1 - portionSold);
  const unrealizedValue = costBasisRemaining + unrealizedPnl;

  const strat = getStrategyMeta(p);

  return (
    <tr className="border-b dark:border-gray-700 last:border-0 hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer" onClick={() => onChart(p)}>
      <td className="px-3 py-2">
        <div className="flex items-center gap-1.5">
          <span className="font-medium text-gray-900 dark:text-white text-sm">{p.tokenSymbol}</span>
          <span className={`px-1 py-0.5 text-[8px] font-bold rounded ${strat.badge}`}>{strat.label}</span>
          {(p.isMoonbag || p.investmentRecovered) && (
            <span className="px-1 py-0.5 text-[8px] font-bold rounded bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300">MOON</span>
          )}
        </div>
      </td>
      <td className="px-2 py-2">
        <span className={`px-1 py-0.5 text-[10px] font-medium rounded bg-${CHAIN_COLORS[p.chain] || 'gray'}-100 dark:bg-${CHAIN_COLORS[p.chain] || 'gray'}-900/30 text-${CHAIN_COLORS[p.chain] || 'gray'}-800 dark:text-${CHAIN_COLORS[p.chain] || 'gray'}-300`}>
          {CHAIN_LABELS[p.chain] || p.chain}
        </span>
      </td>
      <td className="px-2 py-2 text-right">
        <div className="text-[10px] text-gray-500 dark:text-gray-400">{formatNum(invested, 3)}</div>
      </td>
      <td className="px-2 py-2 text-right">
        <div className="text-[10px] text-green-600">{formatNum(realized, 3)}</div>
        {unrealizedValue > 0 && <div className="text-[9px] text-blue-400">+{formatNum(unrealizedValue, 3)} ur</div>}
      </td>
      <td className={`px-2 py-2 text-right font-medium text-sm ${totalPnl >= 0 ? 'text-green-600' : 'text-red-600'}`}>
        <div>{totalPnl >= 0 ? '+' : ''}{formatNum(totalPnl, 3)}</div>
        <div className="text-[10px]">{totalPnlPct >= 0 ? '+' : ''}{totalPnlPct.toFixed(1)}%</div>
      </td>
      <td className={`px-2 py-2 text-right font-bold text-sm ${multiple >= 2 ? 'text-green-600' : multiple < 1 ? 'text-red-600' : 'text-gray-900 dark:text-white'}`}>
        {multiple.toFixed(2)}x
      </td>
    </tr>
  );
}

export function PositionsPanel({ title, accentColor, positions, filter, setFilter, onChart, emptyMsg, analyticsMode }) {
  const borderColor = accentColor === 'green' ? 'border-green-500' : 'border-yellow-500';
  const bgAccent = accentColor === 'green' ? 'bg-green-500' : 'bg-yellow-500';
  const textAccent = accentColor === 'green' ? 'text-green-600 dark:text-green-400' : 'text-yellow-600 dark:text-yellow-400';

  return (
    <div className={`bg-white dark:bg-gray-800 rounded-lg shadow border-t-2 ${borderColor}`}>
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-2">
          <span className={`w-2.5 h-2.5 rounded-full ${bgAccent}`} />
          <h3 className={`text-sm font-bold ${textAccent}`}>{title}</h3>
          <span className="text-xs text-gray-400 dark:text-gray-500">({positions.length})</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex gap-1">
            {['active', 'all', 'closed'].map((f) => (
              <button key={f} onClick={() => setFilter(f)}
                className={`px-2 py-0.5 text-[10px] rounded ${
                  filter === f ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 font-medium' : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}>
                {f.charAt(0).toUpperCase() + f.slice(1)}
              </button>
            ))}
          </div>
          <Link
            to={`/trading/masterchef${analyticsMode ? `?mode=${analyticsMode}` : ''}`}
            className="text-[10px] text-blue-500 hover:underline whitespace-nowrap"
          >
            View all →
          </Link>
        </div>
      </div>

      {positions.length === 0 ? (
        <div className="p-6 text-center text-xs text-gray-400 dark:text-gray-500">{emptyMsg}</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] text-gray-500 dark:text-gray-400 border-b dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 sticky top-0">
                <th className="text-left px-3 py-1.5">Token</th>
                <th className="text-left px-2 py-1.5">Chain</th>
                <th className="text-right px-2 py-1.5">Inv</th>
                <th className="text-right px-2 py-1.5">Real</th>
                <th className="text-right px-2 py-1.5">P&L</th>
                <th className="text-right px-2 py-1.5">Mult</th>
              </tr>
            </thead>
            <tbody>
              {positions.map((p) => (
                <PositionRow key={p._id} p={p} onChart={onChart} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
